import React, { useCallback, useMemo } from 'react';
import { t } from 'ttag.macro';

import { Friend_Status_Type_Enum, UserProfileFragment } from '../../graphql/schema';
import SubmitButton from '../core/SubmitButton';
import { apiFunction } from '../../lib/api';
import { find } from 'lodash';

interface Props {
  profile?: UserProfileFragment;
  userId: string;
  authUserId: string;
  refreshProfile: () => void;
}

export default function FriendStatusComponent({ profile, userId, authUserId, refreshProfile }: Props) {
  const friendState: Friend_Status_Type_Enum = useMemo(() => {
    if (!profile) {
      return Friend_Status_Type_Enum.None;
    }
    if (find(profile.friends, u => u.user?.id === authUserId)) {
      return Friend_Status_Type_Enum.Friend;
    }
    if (find(profile.sent_requests, u => u.user?.id === authUserId)) {
      return Friend_Status_Type_Enum.Sent;
    }
    if (find(profile.received_requests, u => u.user?.id === authUserId)) {
      return Friend_Status_Type_Enum.Received;
    }
    return Friend_Status_Type_Enum.None;
  }, [profile, authUserId]);
  const updateFriendRequest = apiFunction('social-updateFriendRequest');
  const onSubmit = useCallback(async (action: 'request' | 'revoke') => {
    const response = await updateFriendRequest({ userId, action });
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    refreshProfile();
    return undefined;
  }, [updateFriendRequest, refreshProfile, userId]);
  const sendFriendRequest = useCallback(() => {
    return onSubmit('request');
  }, [onSubmit])
  const revokeFriendRequest = useCallback(async() => {
    return onSubmit('revoke');
  }, [onSubmit])
  switch (friendState) {
    case Friend_Status_Type_Enum.None:
      return (
        <SubmitButton onSubmit={sendFriendRequest} variant="contained" color="primary">
          {t`Send Friend Request`}
        </SubmitButton>
      );
    case Friend_Status_Type_Enum.Sent:
      return (
        <SubmitButton onSubmit={sendFriendRequest} variant="contained" color="primary">
          {t`Confirm Friend`}
        </SubmitButton>
      );
    case Friend_Status_Type_Enum.Received:
      return (
        <SubmitButton onSubmit={revokeFriendRequest} variant="outlined" color="secondary">
          {t`Cancel Friend Request`}
        </SubmitButton>
      );
    case Friend_Status_Type_Enum.Friend:
      return (
        <SubmitButton onSubmit={revokeFriendRequest} variant="outlined" color="secondary">
          {t`Cancel Friendship`}
        </SubmitButton>
      );
  }
}
